import { useState } from "react";
import { DashboardLayout } from "./Dashboard";
import { cn } from "@/lib/utils";
import { CreditCard, Check, Download } from "lucide-react";

interface Invoice {
    id: string;
    date: string;
    amount: number;
    status: "paid" | "pending" | "failed";
}

export function BillingPage() {
    const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");

    const currentPlan = {
        name: "Pro",
        price: 99,
        requests: 500000,
        used: 247892,
    };

    const plans = [
        {
            name: "Free",
            price: 0,
            requests: "10K",
            features: ["Current weather", "3-day forecast", "60 req/min"],
            current: false,
        },
        {
            name: "Developer",
            price: billingPeriod === "annual" ? 24 : 29,
            requests: "100K",
            features: ["7-day forecast", "Historical (7 days)", "300 req/min"],
            current: false,
        },
        {
            name: "Pro",
            price: billingPeriod === "annual" ? 79 : 99,
            requests: "500K",
            features: ["14-day forecast", "Historical (30 days)", "Alerts & AQI", "1K req/min"],
            current: true,
        },
        {
            name: "Scale",
            price: billingPeriod === "annual" ? 239 : 299,
            requests: "2M",
            features: ["Everything in Pro", "Priority support", "10K req/min", "SLA"],
            current: false,
        },
    ];

    const invoices: Invoice[] = [
        { id: "INV-001", date: "Jan 1, 2026", amount: 99, status: "paid" },
        { id: "INV-002", date: "Dec 1, 2025", amount: 99, status: "paid" },
        { id: "INV-003", date: "Nov 1, 2025", amount: 99, status: "paid" },
    ];

    const paymentMethod = {
        brand: "Visa",
        last4: "4242",
        expiry: "12/28",
    };

    return (
        <DashboardLayout>
            {/* Header */}
            <header className="bg-white border-b border-slate-200 px-8 py-4">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-xl font-semibold text-slate-800">Billing</h1>
                        <p className="text-sm text-slate-500">
                            Manage your subscription and payment methods
                        </p>
                    </div>
                </div>
            </header>

            <div className="flex-1 overflow-auto p-8">
                {/* Current Plan Card */}
                <div className="bg-gradient-to-r from-sky-500 to-sky-600 rounded-xl p-6 text-white mb-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-sky-100 text-sm">Current Plan</p>
                            <h2 className="text-3xl font-bold mb-1">{currentPlan.name}</h2>
                            <p className="text-sky-100">
                                ${currentPlan.price}/month • {currentPlan.requests.toLocaleString()} requests
                            </p>
                        </div>
                        <button className="px-4 py-2 bg-white text-sky-600 text-sm font-medium rounded-lg hover:bg-sky-50 transition-colors">
                            Manage Plan
                        </button>
                    </div>
                    <div className="mt-6">
                        <div className="flex justify-between text-sm mb-2">
                            <span>Usage this period</span>
                            <span>
                                {currentPlan.used.toLocaleString()} / {currentPlan.requests.toLocaleString()}
                            </span>
                        </div>
                        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-white rounded-full"
                                style={{ width: `${(currentPlan.used / currentPlan.requests) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Plans Grid */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-slate-800">Available Plans</h3>
                        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                            <button
                                onClick={() => setBillingPeriod("monthly")}
                                className={cn(
                                    "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                                    billingPeriod === "monthly"
                                        ? "bg-white text-slate-800 shadow-sm"
                                        : "text-slate-600"
                                )}
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingPeriod("annual")}
                                className={cn(
                                    "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                                    billingPeriod === "annual"
                                        ? "bg-white text-slate-800 shadow-sm"
                                        : "text-slate-600"
                                )}
                            >
                                Annual <span className="text-emerald-600 text-xs">-20%</span>
                            </button>
                        </div>
                    </div>

                    <div className="grid grid-cols-4 gap-4">
                        {plans.map((plan) => (
                            <div
                                key={plan.name}
                                className={cn(
                                    "bg-white rounded-xl border-2 p-5 transition-all",
                                    plan.current
                                        ? "border-sky-500 shadow-lg shadow-sky-100"
                                        : "border-slate-200 hover:border-slate-300"
                                )}
                            >
                                {plan.current && (
                                    <span className="inline-block px-2 py-0.5 bg-sky-100 text-sky-700 text-xs font-medium rounded-full mb-2">
                                        Current
                                    </span>
                                )}
                                <h4 className="text-lg font-semibold text-slate-800">{plan.name}</h4>
                                <div className="mt-2 mb-4">
                                    <span className="text-3xl font-bold text-slate-800">${plan.price}</span>
                                    <span className="text-slate-500">/mo</span>
                                </div>
                                <p className="text-sm text-slate-600 mb-4">{plan.requests} requests/mo</p>
                                <ul className="space-y-2 mb-4">
                                    {plan.features.map((feature) => (
                                        <li key={feature} className="flex items-center gap-2 text-sm text-slate-600">
                                            <Check className="w-4 h-4 text-emerald-500" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className={cn(
                                        "w-full py-2 text-sm font-medium rounded-lg transition-colors",
                                        plan.current
                                            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                    )}
                                    disabled={plan.current}
                                >
                                    {plan.current ? "Current Plan" : "Switch Plan"}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Payment Method & Invoices */}
                <div className="grid grid-cols-2 gap-6">
                    {/* Payment Method */}
                    <div className="bg-white rounded-xl border border-slate-200">
                        <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center">
                            <h3 className="font-medium text-slate-800">Payment Method</h3>
                            <button className="text-sm text-sky-600 hover:text-sky-700">Edit</button>
                        </div>
                        <div className="p-5">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-8 bg-slate-100 rounded flex items-center justify-center text-slate-600">
                                    <CreditCard className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="font-medium text-slate-800">
                                        {paymentMethod.brand} •••• {paymentMethod.last4}
                                    </p>
                                    <p className="text-sm text-slate-500">Expires {paymentMethod.expiry}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Invoices */}
                    <div className="bg-white rounded-xl border border-slate-200">
                        <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center">
                            <h3 className="font-medium text-slate-800">Recent Invoices</h3>
                            <button className="text-sm text-sky-600 hover:text-sky-700">View All</button>
                        </div>
                        <div className="divide-y divide-slate-100">
                            {invoices.map((invoice) => (
                                <div key={invoice.id} className="px-5 py-3 flex justify-between items-center">
                                    <div>
                                        <p className="font-medium text-slate-800">${invoice.amount}</p>
                                        <p className="text-xs text-slate-500">{invoice.date}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span
                                            className={cn(
                                                "px-2 py-0.5 text-xs font-medium rounded-full",
                                                invoice.status === "paid"
                                                    ? "bg-emerald-100 text-emerald-700"
                                                    : invoice.status === "pending"
                                                        ? "bg-amber-100 text-amber-700"
                                                        : "bg-red-100 text-red-700"
                                            )}
                                        >
                                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                        </span>
                                        <button className="text-slate-500 hover:text-slate-700">
                                            <Download className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
}

export default BillingPage;
